import React from 'react';
import { Sun, Activity, Sparkles, DollarSign, Users, Target, Clock, BarChart3, Bot, Layers, TrendingUp, Briefcase } from 'lucide-react';

const GetStarted = ({ onBack }) => {
  const sections = [
    {
      icon: Sun,
      title: "Today — Your Daily Command Center",
      content: "Open Today every morning to see where things stand. It shows your Momentum Score (auto-tracked from your activity), top Milestones, today's Schedule and focus blocks, and your current Cash Runway — all at a glance so you can start the day with clarity.",
      color: 'rgba(45,212,191,0.12)',
      accent: '#2dd4bf',
    },
    {
      icon: Activity,
      title: "Build — Where the Work Happens",
      content: "Manage your issue board, track milestones, and run focus sessions all from one place. The Board holds your tasks and priorities in a Kanban-style view. Milestones let you set goals with deadlines and track progress. Use Focus Sessions to block time for deep work — they show up on Today automatically.",
      color: 'rgba(99,102,241,0.12)',
      accent: '#6366f1',
    },
    {
      icon: Sparkles,
      title: "AI Co-Founder — Always Available",
      content: "Your AI co-founder is context-aware — it knows your milestones, runway, and build progress. Ask it anything: pricing, positioning, hiring decisions, pivots. Use it to think through strategy or unblock yourself when you're stuck. Access it from the AI tab or via the floating shortcut on any page.",
      color: 'rgba(168,85,247,0.12)',
      accent: '#a855f7',
    },
    {
      icon: DollarSign,
      title: "Money — Financial Health at a Glance",
      content: "Stay on top of cash on hand, monthly burn rate, and months of runway remaining. Log your income and expenses to keep your financial picture accurate. The Money tab surfaces key financial milestones and gives you a clear view of how long you can operate — so you're never caught off guard.",
      color: 'rgba(251,191,36,0.12)',
      accent: '#fbbf24',
    },
    {
      icon: Users,
      title: "Team — Keep Everyone Aligned",
      content: "Add team members, assign roles, and manage workload so nothing falls through the cracks. Set team-wide Objectives to track collective progress. Use the Hiring Plan to map out the roles you need to fill as you grow. A clear team view helps you lead with confidence.",
      color: 'rgba(52,211,153,0.12)',
      accent: '#34d399',
    },
  ];

  const tips = [
    {
      title: "Set your #1 Milestone first",
      desc: "Add your current biggest goal in Build → Milestones. It shows up on Today and gives the AI Co-Founder context about what you're working toward.",
    },
    {
      title: "Log cash on hand",
      desc: "Go to Money and enter your current balance. Founders+ will calculate your runway automatically as you log expenses.",
    },
    {
      title: "Schedule a focus block daily",
      desc: "Create a time block in Build → Focus Sessions. It appears on your Today schedule and contributes to your Momentum Score.",
    },
    {
      title: "Use AI for real decisions",
      desc: "The AI Co-Founder is most useful when you give it context. Ask it to help you think through a pricing model, a hiring decision, or why growth has stalled.",
    },
    {
      title: "Check Today every morning",
      desc: "Today is designed to be your first stop each day — not a dashboard you check once a week. Make it a habit and your Momentum Score will reflect it.",
    },
    {
      title: "Add your team early",
      desc: "Even if it's just you and one co-founder, adding team members unlocks workload tracking and objective-setting in the Team tab.",
    },
  ];

  const faqs = [
    {
      q: "What does the AI Daily Brief do?",
      a: "Every day, Today generates an AI Daily Brief that synthesises data across your workspace — open issues, runway, team workload, and milestone deadlines. It surfaces the most important things to pay attention to right now, so you don't have to manually scan every tab.",
    },
    {
      q: "How does the AI Co-Founder get context about my startup?",
      a: "The AI reads your milestones, runway, open issues, and team data in real time. It also surfaces suggested prompts based on what it detects — like a low runway warning or overdue issues. The more data you add to your workspace, the sharper its answers become. You can also set your founder type (aspiring, early-stage, engineer, etc.) to personalise its tone and advice.",
    },
    {
      q: "How does runway get calculated in the Money tab?",
      a: "Runway is your current cash on hand divided by your average monthly burn rate. Log your income and expenses in the Money tab (tap 'Log transaction') and Founders+ calculates burn automatically. The 6-month cash forecast chart updates as you add transactions. You'll also see a low-runway alert on Today if you're running short.",
    },
    {
      q: "What is the Investor Pipeline in the Money tab?",
      a: "The Investor Pipeline lets you track fundraising prospects through five stages: Lead → Contacted → Meeting → Due Diligence → Term Sheet. Add each investor as a prospect and move them through stages so you always know where each conversation stands.",
    },
    {
      q: "What's the difference between the Board and Milestones in Build?",
      a: "The Board is your day-to-day issue tracker — tasks move through Backlog → To Do → In Progress → Done in a Kanban view. You can add subtasks, assignees, due dates, labels, image attachments, and linked issues. Milestones are bigger goals with a target date and a progress percentage, usually made up of several Board issues.",
    },
    {
      q: "How does Team Chat work?",
      a: "Team Chat lives in the Team tab and has channels (#general, #build, #design, etc.) plus direct messages. Messages support replies with quote highlighting. You can search across all channels and see unread badges. It's designed for quick async coordination without leaving Founders+.",
    },
    {
      q: "What is Demo Mode?",
      a: "Demo Mode is a read-only preview with sample data that auto-cycles through Today, Build, Money, and Team — about 22 seconds per loop. Nothing is saved to your account. Access it from the dashboard header or the onboarding screen. It's useful for sharing the product with a co-founder or investor without giving them access to your real workspace.",
    },
    {
      q: "Can I use Founders+ solo (no team)?",
      a: "Absolutely. Today, Build, AI, and Money work fully for solo founders. The Team tab is there when you're ready — it won't get in your way until then.",
    },
    {
      q: "Is my data secure?",
      a: "Yes. All data is encrypted in transit and at rest. Your workspace is private to your account. See our Privacy Policy for full details.",
    },
  ];

  return (
    <div className="min-h-screen py-10 px-4" style={{ backgroundColor: 'var(--bg-base)' }}>
      <div className="max-w-3xl mx-auto">
        {/* Back button */}
        <button
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-sm font-medium transition-colors"
          style={{ color: 'var(--accent-primary)' }}
        >
          <span>←</span> Back
        </button>

        <div className="p-8 rounded-2xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Get Started with Founders+</h1>
          <p className="text-sm mb-8" style={{ color: 'var(--text-muted)' }}>
            Founders+ is your all-in-one founder OS — track momentum, manage your build, understand your finances, and get strategic support from an AI co-founder.
          </p>

          {/* Core Sections */}
          <div className="mb-12">
            <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>The Five Sections</h2>
            <div className="space-y-6">
              {sections.map((section, idx) => {
                const Icon = section.icon;
                return (
                  <div key={idx} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: section.color, color: section.accent }}
                      >
                        <Icon size={20} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                        {section.title}
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {section.content}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick-start tips */}
          <div className="mb-12">
            <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>Quick-Start Tips</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {tips.map((tip, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: 'var(--interactive-default)', border: '1px solid var(--card-border)' }}
                >
                  <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                    {tip.title}
                  </h3>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {tip.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-12">
            <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border-l-2" style={{ borderColor: 'var(--accent-primary)' }}>
                  <div className="pl-4">
                    <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                      {faq.q}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {faq.a}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div
            className="p-6 rounded-lg text-center"
            style={{ backgroundColor: 'var(--interactive-default)', border: '1px solid var(--accent-primary)' }}
          >
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Ready to build?
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Head to Today, add your first milestone, and let Founders+ track your momentum from day one.
            </p>
            <button
              onClick={onBack}
              className="px-6 py-2 rounded-lg font-medium transition-opacity"
              style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Go to Dashboard
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
          <p>Have questions? Reach out at <a href="mailto:foundersplus2025@gmail.com" style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}>foundersplus2025@gmail.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default GetStarted;
